import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import type { BoundingBoxContentType } from '../shared/bounding-box-content.js';
import { Point } from './Point.js';

@Entity()
export class BoundingBox extends AdornisEntity {
  public static override _class = 'BoundingBox';

  constructor(doc: PartialEntityData<BoundingBox>) {
    super(doc);
  }

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() }) _id!: string;

  @Field(type => Float)
  public pageNumber: Maybe<number>;

  @Field(type => String)
  public imageId: Maybe<string>;

  @Field(type => Boolean)
  public isHandwritten: Maybe<boolean>;

  // What this region visually contains, as classified by the OCR step:
  // 'image' (drawing/sketch/plot, no transcribable text), 'text', or 'both'.
  // Drives the workstep input strategy and the frontend display.
  @Field(type => String)
  public contentType: Maybe<BoundingBoxContentType>;

  @Field(type => String)
  public ocrExtractedText: Maybe<string>;

  @Field(type => Float)
  public confidence: Maybe<number>;

  @Field(type => Boolean)
  public approved: Maybe<boolean>;

  // Durable "OCR is running for this box right now" flag — written by updateSubmissionTaskMutation
  // BEFORE it calls out to OCR and cleared in the same write that lands the result (or on failure).
  // Read by the client straight off this field via the existing submissions subscription, so a
  // canvas that was closed, reloaded, or never open for the start of the request still shows the
  // correct state the moment it (re)mounts — unlike a client-side "isSaving" flag, which only exists
  // for as long as the specific browser tab that started the request stays around to track it.
  @Field(type => Boolean)
  public ocrRerunning: Maybe<boolean>;

  @Field(type => Point, { default: v => v ?? new Point({}) })
  public topLeft!: Point;

  @Field(type => Point, { default: v => v ?? new Point({}) })
  public bottomRight!: Point;
}
